import { pick, uniq, uniqWith } from 'es-toolkit';

import { getUserVisibleOrDefaultExceptionMessage } from '@paragon/client-tools';
import {
  AnalysisSettings,
  ComponentDefinitionType,
  ComponentDesign,
  ComponentDesignTimestamps,
  ComponentTemplateType,
  ComponentType,
  DEFAULT_ORGANIZATION_SETTINGS,
  FeaturePatternTrussSettings,
  getLumberDescription,
  isFeaturePatternTruss,
  isProfileTruss,
  isTruss,
  LoadSettings,
  Lumber,
  MaterialSelection,
  MaterialSettings,
  materialSettingsLumberProperties,
  MaterialSettingsPair,
  MemberAnalysisInfo,
  MemberAnalysisInputs,
  OrganizationSettings,
  Profile,
  ProfileDefaults,
  ProfileTrussSettings,
  StockLengthPrice,
  WebbingInfo,
} from '@paragon/contract-library';
import {
  CreateFeaturePatternTrussRequest,
  designService,
  RegenerateTrussFromSettingsResponse,
} from '@paragon/data-connections';
import { getDefaultProfileDefaults } from '@paragon/data-connections/lib/design-service/defaults';
import { distanceEquals } from '@paragon/geometry-library';
import { getLayoutModelsWithPendingChanges, getParentGuids } from '@paragon/layout-models';
import {
  getLoggedInUsersOrganizationMaterialSet,
  getLumberCode,
  getLumberLists,
  lumbersAreEqual,
  whenOrganizationMaterialSetLoaded,
} from '@paragon/material-lists';
import { enqueueError, enqueueWarning } from '@paragon/notification-tools';
import { addSpinnerMessage, removeSpinnerMessage, runWithSpinner } from '@paragon/task-spinner';
import { assertExhaustiveSwitch, notNullish } from '@paragon/utilities';

import { SaveAction } from '../../components/interface/subpanels/settings/SettingsSaveButton';
import { calculateProfileLineGeometry, getProfileIsClosed } from '../../library/profiles/profileManipulation';
import { getDefaultShapeDetails } from '../../library/profiles/profileTrussDefaults';
import {
  createDefaultProfileTrussSettings,
  createProfileSettingsForShape,
} from '../../library/profiles/profileTrussSettings';
import { findNextName } from '../../library/utilities/findNextName';
import { focusCameraOnInsertionProfile } from '../../three/ThreeJsSceneActions';
import { RootState } from '../index';
import { waitForComponentDesignToFinishSaving } from '../middlewares/saveComponentDesignMiddleware';
import { getBlockingOperation } from '../selectors/blockingOperationSelectors';
import { getActiveComponentDesign } from '../selectors/getActiveComponentDesignSelector';
import { getOrganizationForOpenContext } from '../selectors/getOrganizationForInsertingComponent';
import { getComponentDesignBeingInserted } from '../selectors/insertionObjectSelectors';
import { isAnalyzingComponentRequestActive } from '../selectors/isAnalyzingComponentRequestActive';
import { isParagonJobOpen } from '../selectors/jobContextSelectors';
import { getTopChordWidthDuringProfileInsertion } from '../selectors/profileSelectors';
import { getExistingComponentDesignNames } from '../selectors/uniqueComponentNameSelectors';
import {
  applicationOnRegeneratingComponentDesign,
  clearInsertingObject,
  ComponentInsertionShape,
  ComponentInsertionStage,
  ConfigurableShapeDetails,
  finishInsertionComponentDownload,
  InsertingComponentDesignInfo,
  setComponentInsertionMemberAnalysisInfo,
  setInsertionComponentStage,
  setInsertionComponentType,
  startInsertingComponentDesign,
  startInsertionComponentDownload,
  switchInsertionComponentDesignType,
  updateProfileInsertionWebbingInfo,
  updateShapeDetails,
} from '../slices/applicationSlice';
import { receiveRegenerateFromSettingsResponse } from '../slices/componentDesignsSlice';
import { setComponentDesignTimestamps } from '../slices/componentDesignTimestampsSlice';
import { clearSelection } from '../slices/selectionsSlice';
import {
  abortSelectFiles,
  addComponentDesignToProject,
  addNewComponentDesignsToJobLayoutModel,
  createProfileOfActiveComponentDesign,
} from './appActions';
import {
  loadAdditionalComponentDesignInformation,
  loadAnalysisSettings,
  loadDefaultMaterialSettingsAndPrices,
  loadFeaturePatternSettings,
  loadLoadSettings,
  loadMaterialSettings,
  loadMaterialSettingsAndPrices,
  loadProfileDefaults,
  receiveComponentDesign,
  unloadInsertComponentDesign,
} from './loadActions';
import { cleanProfileTrussSettings } from './profileActions';
import { selectItem } from './selectActions';
import {
  loadOrganizationSettingsIfNeeded,
  saveAnalysisSettings,
  saveLoadSettings,
  saveMaterialSettings,
  setAnalysisSettingsForComponentDesigns,
  setLoadSettingsForComponentDesigns,
} from './settingsActions';
import { AsyncThunkActionReturn, AsyncThunkActionVoid, ThunkActionVoid } from './thunkActions';

/**
 * Unload and deselect previously loaded component
 */
export const unloadCurrentInsertionComponentDesign = (): ThunkActionVoid => (dispatch, getState) => {
  const oldInsertion = getComponentDesignBeingInserted(getState());
  if (oldInsertion) {
    dispatch(unloadInsertComponentDesign(oldInsertion));
    dispatch(clearSelection());
  }
};

export const startInsertComponentDesign =
  (passedParentGuid?: string): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const blockingOperation = getBlockingOperation(getState());
    if (blockingOperation) {
      dispatch(enqueueError(`Cannot begin component creation while ${blockingOperation.userVisibleDescription}.`));
      return;
    }

    const parentGuid = passedParentGuid ?? getDefaultParentGuidForNewComponentDesign(getState());

    const materialSelection =
      parentGuid != null
        ? getLayoutModelsWithPendingChanges(getState())[parentGuid].materialSelection
        : await dispatch(getDefaultMaterialSelectionForOrganization());
    if (!materialSelection) {
      dispatch(enqueueError('Material lists not loaded. Try refreshing your web page.'));
      return;
    }

    const componentTypes = Object.values(getState().componentDesigns.data).map(
      (componentDesign) => componentDesign.component.componentType,
    );
    const numberOfFloorTrusses = componentTypes.filter((componentType) => componentType === 'FloorTruss').length;
    const numberOfRoofTrusses = componentTypes.filter((componentType) => componentType === 'RoofTruss').length;
    const componentType = numberOfFloorTrusses > numberOfRoofTrusses ? 'FloorTruss' : 'RoofTruss';

    const definitionType = componentType === 'FloorTruss' ? 'FeaturePattern' : 'Profile';
    const shape =
      definitionType === 'Profile'
        ? 'FreeformProfile'
        : definitionType === 'FeaturePattern'
          ? 'FeaturePattern'
          : assertExhaustiveSwitch(definitionType);

    const templateType = 'default';

    const spinnerId = dispatch(addSpinnerMessage('Getting truss settings...'));

    let shapeDetails: ConfigurableShapeDetails | null;
    let profileDefaults: ProfileDefaults | null;
    let designSettings: FeaturePatternTrussSettings | ProfileTrussSettings;
    let loadSettings: LoadSettings;
    let analysisSettings: AnalysisSettings;
    let materialSettings: MaterialSettingsPair;

    try {
      const inProject = getState().application.openContext?.type === 'project';

      const profileDefaultsPromise =
        definitionType === 'Profile'
          ? parentGuid
            ? dispatch(loadProfileDefaults(parentGuid))
            : getDefaultProfileDefaults()
          : null;

      [[shapeDetails, designSettings], loadSettings, analysisSettings, materialSettings] = await Promise.all([
        dispatch(
          getInitialComponentShapeDetailsAndSettings(
            componentType,
            definitionType,
            templateType,
            shape,
            parentGuid,
            profileDefaultsPromise,
          ),
        ),
        inProject ? dispatch(loadLoadSettings(parentGuid!)) : designService.defaults.getDefaultLoadSettings(),
        inProject ? dispatch(loadAnalysisSettings(parentGuid!)) : designService.defaults.getDefaultAnalysisSettings(),
        inProject
          ? dispatch(loadMaterialSettingsAndPrices(parentGuid!))
          : dispatch(loadDefaultMaterialSettingsAndPrices()),
      ]);
      profileDefaults = await profileDefaultsPromise;
    } catch {
      dispatch(enqueueError('Unable to load truss settings'));
      return;
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }

    // We do all non-committal async work up-front before checking this, in the off-chance analysis finishes while that
    // work is being processed.
    if (isAnalyzingComponentRequestActive(getState())) {
      dispatch(enqueueError('Cannot create a new truss while a truss is analyzing.'));
      return;
    }

    dispatch(
      startInsertingComponentDesign({
        parentGuid,
        previouslySelectedComponentDesignGuid: getState().selections.activeComponentDesignGuid,

        componentType,
        definitionType,
        templateType,
        shape,

        shapeDetails,

        designSettings,
        profileDefaults,
        materialSelection,

        loadSettings,
        analysisSettings,
        materialSettings,
      }),
    );

    if (definitionType === 'Profile') {
      dispatch(clearSelection());
      focusCameraOnInsertionProfile();
    } else if (definitionType === 'FeaturePattern') {
      await dispatch(updateInsertingTrussFromSettings({ preview: true, focusCameraOnTruss: true }));
    } else {
      assertExhaustiveSwitch(definitionType);
    }
  };

function getDefaultParentGuidForNewComponentDesign(state: RootState): string | null {
  switch (state.application.openContext?.type) {
    case 'project':
      return state.application.openContext.projectGuid;

    case 'station':
    case 'schedule-station':
      return state.application.openContext.stationGuid;

    case 'shared-component-set': // not allowed to insert components into shared component sets
    case 'job': // special case as context is identified by a numerical ID and not a GUID
    case undefined: // no parent guid is fine / expected with no context
      return null;

    default:
      assertExhaustiveSwitch(state.application.openContext);
  }
}

const getDefaultMaterialSelectionForOrganization =
  (): AsyncThunkActionReturn<MaterialSelection | null> => async (dispatch, getState) => {
    let defaultMaterialSelection = getLoggedInUsersOrganizationMaterialSet(getState())?.defaultMaterials;
    if (defaultMaterialSelection) return defaultMaterialSelection;

    // If material selection is not set, show spinner while waiting for them to load.
    const spinnerId = dispatch(addSpinnerMessage('Loading organization materials...'));
    try {
      await whenOrganizationMaterialSetLoaded;
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }

    defaultMaterialSelection = getLoggedInUsersOrganizationMaterialSet(getState())?.defaultMaterials;
    return defaultMaterialSelection ?? null;
  };

const getInitialComponentShapeDetailsAndSettings =
  (
    componentType: ComponentType,
    definitionType: ComponentDefinitionType,
    templateType: ComponentTemplateType,
    shape: ComponentInsertionShape,
    parentGuid: string | null,
    profileDefaultsPromise: Promise<ProfileDefaults> | null, // required for profile trusses, null for feature pattern
    manuallySpecifiedName?: string | null,
  ): AsyncThunkActionReturn<[ConfigurableShapeDetails | null, FeaturePatternTrussSettings | ProfileTrussSettings]> =>
  async (dispatch, getState) => {
    let shapeDetails: ConfigurableShapeDetails | null = null;
    let settings: FeaturePatternTrussSettings | ProfileTrussSettings;

    switch (definitionType) {
      case 'FeaturePattern':
        if (templateType === 'default' && parentGuid != null) {
          settings = await dispatch(loadFeaturePatternSettings(parentGuid));
        } else {
          settings = await designService.componentDesigns.getDefaultFeaturePatternTrussSettings(templateType);
        }

        break;

      case 'Profile':
        if (templateType !== 'default') throw new Error('Templates are not yet supported for profile trusses.');

        const profileDefaults = (await profileDefaultsPromise)!;
        shapeDetails = getDefaultShapeDetails(profileDefaults, shape);

        const topChordWidth = getTopChordWidthDuringProfileInsertion(getState());
        if (shapeDetails) {
          settings = createProfileSettingsForShape(shapeDetails, profileDefaults, { topChordWidth });
        } else {
          settings = createDefaultProfileTrussSettings(profileDefaults, { topChordWidth });
        }

        break;

      case 'Wall':
        throw new Error('Walls are not currently supported');

      default:
        assertExhaustiveSwitch(definitionType);
    }

    const name = manuallySpecifiedName ?? getDefaultNameForNewComponent(componentType, getState());

    // The default feature pattern settings specify that the component is a floor truss, but that's not always true.
    // Additionally, inherited settings' `ComponentType` may not match the `ComponentType` specified for this truss.
    settings = { ...settings, componentType, name };

    return [shapeDetails, settings] as const;
  };

const DEFAULT_COMPONENT_NAMES: Record<ComponentType, string> = {
  FloorTruss: 'FT',
  RoofTruss: 'RT',
  Wall: 'W',
};

function getDefaultNameForNewComponent(componentType: ComponentType, state: RootState) {
  return findNextName(getExistingComponentDesignNames(state), { prefix: DEFAULT_COMPONENT_NAMES[componentType] });
}

export const setComponentTypeForFeaturePatternTrussInsertion =
  (componentType: ComponentType): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const defaultNameForComponentType = getDefaultNameForNewComponent(componentType, getState());
    dispatch(setInsertionComponentType({ componentType, name: defaultNameForComponentType }));
    await dispatch(updateInsertingTrussFromSettings({ preview: true }));
  };

export const chooseInsertionComponentType =
  (
    componentType: ComponentType,
    definitionType: ComponentDefinitionType,
    templateType: ComponentTemplateType,
    shape: ComponentInsertionShape,
  ): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const { insertingObjectInfo } = getState().application;
    if (insertingObjectInfo?.type !== 'componentDesign')
      throw new Error('Expected to be in component design insertion');

    // If the user has changed the truss name, we should try to preserve it
    const currentName = insertingObjectInfo.designSettings.name;
    const manuallySpecifiedName = Object.values(DEFAULT_COMPONENT_NAMES).some((defaultName) =>
      currentName.startsWith(`${defaultName}-`),
    )
      ? null
      : currentName;

    let shapeDetails: ConfigurableShapeDetails | null;
    let designSettings: FeaturePatternTrussSettings | ProfileTrussSettings;

    const spinnerId = dispatch(addSpinnerMessage('Getting truss settings...'));

    try {
      const profileDefaultsPromise = insertingObjectInfo.profileDefaults
        ? Promise.resolve(insertingObjectInfo.profileDefaults)
        : definitionType === 'Profile'
          ? insertingObjectInfo.parentGuid
            ? dispatch(loadProfileDefaults(insertingObjectInfo.parentGuid))
            : getDefaultProfileDefaults()
          : null;

      [shapeDetails, designSettings] = await dispatch(
        getInitialComponentShapeDetailsAndSettings(
          componentType,
          definitionType,
          templateType,
          shape,
          insertingObjectInfo.parentGuid,
          profileDefaultsPromise,
          manuallySpecifiedName,
        ),
      );
    } catch {
      dispatch(enqueueError('Unable to load truss settings'));
      return;
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }

    // We need to call this before `application.insertingObjectInfo.loadedInfo` gets cleared by
    // `switchInsertionComponentDesignType`
    dispatch(unloadCurrentInsertionComponentDesign());

    dispatch(
      switchInsertionComponentDesignType({
        definitionType,
        componentType,
        templateType,
        shape,
        shapeDetails,
        designSettings,
      }),
    );

    // When changing to a freeform profile insertion, we don't want to jump to stage two since the profile is drawn
    //   directly in stage one. We also don't want to update the inserting truss since the profile is not yet defined.
    // This is a special case only hit when switching back to stage one of component insertion.
    if (shape === 'FreeformProfile') {
      focusCameraOnInsertionProfile();
      return;
    }

    dispatch(setInsertionComponentStage('ShapeDetails'));
    await dispatch(updateInsertingTrussFromSettings({ preview: true, focusCameraOnTruss: true }));
  };

const insertingComponentDesignHasBeenCancelled = (componentDesign: ComponentDesign, state: RootState) => {
  const { insertingObjectInfo } = state.application;

  return (
    !state.componentDesigns.loadedState[componentDesign.guid].committed &&
    !(
      insertingObjectInfo &&
      insertingObjectInfo.type === 'componentDesign' &&
      insertingObjectInfo.loadedInfo.isLoaded &&
      insertingObjectInfo.loadedInfo.guid === componentDesign.guid
    )
  );
};

export const setComponentInsertionStage =
  (componentInsertionStage: ComponentInsertionStage): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const { insertingObjectInfo } = getState().application;
    if (insertingObjectInfo?.type !== 'componentDesign')
      throw new Error('Expected to be inserting a component design.');

    // Sanity check: don't allow progression if the profile is not closed.
    if (
      insertingObjectInfo.definitionType === 'Profile' &&
      insertingObjectInfo.stage === 'ChoosingShape' &&
      !getProfileIsClosed((insertingObjectInfo.designSettings as ProfileTrussSettings).profile)
    ) {
      dispatch(enqueueWarning('Profile must be closed before proceeding.'));
      return;
    }

    if (componentInsertionStage === 'ChoosingShape') {
      // during the first stage freeform profile mode is always on
      await dispatch(chooseInsertionComponentType('RoofTruss', 'Profile', 'default', 'FreeformProfile'));
    }

    // It is important that wwe don't set the stage until after the component type has been changed if required. This
    //    prevents crashes due to a mismatch in definition type.
    dispatch(setInsertionComponentStage(componentInsertionStage));

    if (componentInsertionStage !== 'ChoosingShape') {
      // don't update the truss if we switched to the first stage since the initial profile is not closed
      await dispatch(updateInsertingTrussFromSettings({ preview: true, focusCameraOnTruss: true }));
    }
  };

let profileDefaultsCache: ProfileDefaults | null = null;
async function getCachedProfileDefaults() {
  return profileDefaultsCache || (profileDefaultsCache = await getDefaultProfileDefaults());
}

export const updateShapeDetailsAndRegenerateTruss =
  (shapeDetails: ConfigurableShapeDetails): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const {
      application: { insertingObjectInfo },
    } = getState();

    if (insertingObjectInfo?.type !== 'componentDesign') throw new Error('Component insertion is not active');

    const profileDefaults = await (insertingObjectInfo.parentGuid
      ? dispatch(loadProfileDefaults(insertingObjectInfo.parentGuid))
      : getCachedProfileDefaults());

    const topChordWidth = getTopChordWidthDuringProfileInsertion(getState());
    const profileSettings = createProfileSettingsForShape(shapeDetails, profileDefaults, { topChordWidth });

    dispatch(updateShapeDetails({ shapeDetails, profileSettings }));
    await dispatch(updateInsertingTrussFromSettings({ preview: true, focusCameraOnTruss: true }));
  };

interface TrussDesignInfo {
  readonly componentDesign: ComponentDesign;
  readonly webbingInfo?: WebbingInfo | null;
  readonly memberAnalysisInfo: MemberAnalysisInfo | null;
  readonly timestamps?: ComponentDesignTimestamps;
}

async function getTrussDesign(
  state: RootState,
  insertingObjectInfo: InsertingComponentDesignInfo,
  preview: boolean,
  useWebbingInfoForPreview: boolean | undefined,
  organizationSettings: OrganizationSettings | null,
): Promise<TrussDesignInfo> {
  const organization = getOrganizationForOpenContext(state);

  const lumberLists = getLumberLists(state);
  if (!lumberLists)
    throw new Error('Material lists not loaded. They should have been loaded before starting component insertion.');
  const lumberList = lumberLists[insertingObjectInfo.materialSelection.lumberListGuid];

  const { openContext } = state.application;
  switch (insertingObjectInfo.definitionType) {
    case 'FeaturePattern': {
      const featurePatternSettings = insertingObjectInfo.designSettings as FeaturePatternTrussSettings;
      const materialSettings = insertingObjectInfo.materialSettings.featurePatternMaterialSettings;
      if (!materialSettings) throw new Error('Expected material settings to be set.');
      const stockLengthPrices = getLoadedStockLengthPricesForMaterialSettings(state, materialSettings);

      const memberAnalysisInputs: MemberAnalysisInputs | null =
        insertingObjectInfo.stage === 'StructuralDetails'
          ? {
              lumberList,
              lumberStockLengthPrices: stockLengthPrices,
              loadSettings: insertingObjectInfo.loadSettings,
              jackConnections: insertingObjectInfo.jackConnections,
              jackInformation: insertingObjectInfo.jackInformation,
              analysisSettings: insertingObjectInfo.analysisSettings,
              additionalLoads: insertingObjectInfo?.oldComponentDesign?.additionalLoads ?? null,
            }
          : null;

      const createFeaturePatternTrussRequest: CreateFeaturePatternTrussRequest = {
        featurePatternTrussSettings: featurePatternSettings,
        materialSettings,
        organization,
        materialSelection: insertingObjectInfo.materialSelection,
        memberAnalysisInputs,
      };

      if (!preview) {
        return await designService.componentDesigns.postCreateAndInsertFeaturePatternTruss(
          createFeaturePatternTrussRequest,
          openContext?.type === 'project' ? openContext.projectGuid : undefined,
        );
      }
      return await designService.componentDesigns.postCreateFeaturePatternTrussPreview(
        createFeaturePatternTrussRequest,
      );
    }

    case 'Profile': {
      const profileTrussSettings = insertingObjectInfo.designSettings as ProfileTrussSettings;
      const materialSettings = insertingObjectInfo.materialSettings.profileMaterialSettings;
      if (!materialSettings) throw new Error('Expected material settings to be set.');
      const stockLengthPrices = getLoadedStockLengthPricesForMaterialSettings(state, materialSettings);
      const { scarfCutThreshold } = organizationSettings ?? DEFAULT_ORGANIZATION_SETTINGS;

      const memberAnalysisInputs: MemberAnalysisInputs | null =
        insertingObjectInfo.stage === 'StructuralDetails'
          ? {
              lumberList,
              lumberStockLengthPrices: stockLengthPrices,
              loadSettings: insertingObjectInfo.loadSettings,
              jackConnections: insertingObjectInfo.jackConnections,
              jackInformation: insertingObjectInfo.jackInformation,
              analysisSettings: insertingObjectInfo.analysisSettings,
              additionalLoads: insertingObjectInfo?.oldComponentDesign?.additionalLoads ?? null,
            }
          : null;

      if (!preview) {
        return await designService.componentDesigns.postCreateAndInsertProfileTruss(
          {
            profileTrussSettings,
            materialSettings,
            scarfCutThreshold,
            organization,
            materialSelection: insertingObjectInfo.materialSelection,
            memberAnalysisInputs,
            webbingInfo: insertingObjectInfo.webbingInfo ?? null,
          },
          openContext?.type === 'project' ? openContext.projectGuid : undefined,
        );
      }

      const webbingInfo = insertingObjectInfo.webbingInfo;
      if (useWebbingInfoForPreview && !webbingInfo) throw new Error('No webbing info found.');
      const specifiedWebbingInfo = useWebbingInfoForPreview && webbingInfo ? webbingInfo : null;

      return await designService.componentDesigns.postCreateProfileTrussPreview({
        profileTrussSettings,
        materialSettings,
        scarfCutThreshold,
        organization,
        materialSelection: insertingObjectInfo.materialSelection,
        memberAnalysisInputs,
        webbingInfo: specifiedWebbingInfo,
      });
    }

    case 'Wall':
      throw new Error('Wall truss are not yet implemented');

    default:
      assertExhaustiveSwitch(insertingObjectInfo.definitionType);
  }
}

interface LumberAndStockLengthPrices {
  readonly lumber: Lumber;
  readonly stockLengthPrices: readonly StockLengthPrice[];
}

export function getLoadedStockLengthPricesForMaterialSettings(
  state: RootState,
  materialSettings: MaterialSettings,
): readonly LumberAndStockLengthPrices[] {
  const lumbers = uniqWith(Object.values(pick(materialSettings, materialSettingsLumberProperties)), lumbersAreEqual);

  return getLoadedStockLengthPricesForLumbers(state, lumbers);
}

/**
 * This function assumes that the lumber prices have already been loaded by `loadPrices`. This is important for
 * time-sensitive contexts (like analysis during truss insertion) where we want to make sure that we're not slowing down
 * the current operation by lazily fetching prices.
 */
export function getLoadedStockLengthPricesForLumbers(
  state: RootState,
  lumbers: readonly Lumber[],
): readonly LumberAndStockLengthPrices[] {
  return lumbers.map((lumber) => {
    const stockLengthPrices = state.inventoryPrices.lumberPrices[getLumberCode(lumber)];
    if (!stockLengthPrices) throw new Error(`Expected prices to be loaded for ${getLumberDescription(lumber)}.`);
    return { lumber, stockLengthPrices };
  });
}

interface UpdateInsertingTrussOptions {
  readonly preview: boolean;
  readonly focusCameraOnTruss?: boolean;
  readonly useWebbingInfo?: boolean;
}

export const updateInsertingTrussFromSettings =
  ({ preview, focusCameraOnTruss, useWebbingInfo }: UpdateInsertingTrussOptions): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    let { insertingObjectInfo } = getState().application;
    if (
      !insertingObjectInfo ||
      insertingObjectInfo.type !== 'componentDesign' ||
      (insertingObjectInfo.definitionType !== 'FeaturePattern' && insertingObjectInfo.definitionType !== 'Profile')
    ) {
      throw new Error();
    }

    const spinnerId = dispatch(addSpinnerMessage('Generating truss...'));

    try {
      const downloadId = {};
      dispatch(startInsertionComponentDownload(downloadId));

      let trussDesignInfo;
      try {
        const organizationSettings = await dispatch(loadOrganizationSettingsIfNeeded());
        trussDesignInfo = await getTrussDesign(
          getState(),
          insertingObjectInfo,
          preview,
          useWebbingInfo,
          organizationSettings,
        );
      } catch (error) {
        dispatch(enqueueError(getUserVisibleOrDefaultExceptionMessage(error, 'Error generating component.')));
        return;
      }

      const { componentDesign, timestamps, webbingInfo, memberAnalysisInfo } = trussDesignInfo;

      insertingObjectInfo = getState().application.insertingObjectInfo;

      if (
        !insertingObjectInfo ||
        insertingObjectInfo.type !== 'componentDesign' ||
        !insertingObjectInfo.loadingInfo.isLoading ||
        insertingObjectInfo.loadingInfo.downloadId !== downloadId
      ) {
        return;
      }

      try {
        const hasBeenCancelled = () => insertingComponentDesignHasBeenCancelled(componentDesign, getState());

        await dispatch(
          receiveComponentDesign(componentDesign, {
            commitComponentDesign: !preview,
            replaceWorkingChanges: true,
            isPersisted: !preview,
            skipJackGirderConnections: preview,
            skipSettings: true,
          }),
        );

        if (timestamps) dispatch(setComponentDesignTimestamps({ timestamps }));
        dispatch(setComponentInsertionMemberAnalysisInfo(memberAnalysisInfo));
        if (webbingInfo) dispatch(updateProfileInsertionWebbingInfo(webbingInfo));

        // We need to call this before `application.insertingObjectInfo.loadedInfo` gets cleared by
        // `finishInsertionComponentDownload`
        dispatch(unloadCurrentInsertionComponentDesign());

        dispatch(finishInsertionComponentDownload(componentDesign.guid));

        if (hasBeenCancelled()) {
          dispatch(abortSelectFiles());
          return;
        }

        dispatch(
          selectItem(componentDesign, 'componentDesign', {
            force: true,
            retainCameraPosition: !focusCameraOnTruss,
          }),
        );

        if (hasBeenCancelled()) {
          dispatch(abortSelectFiles());
          return;
        }
      } catch (error) {
        console.error(error);
      }
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }
  };

export const adjustFeaturePatternTrussSettingsForInsertion = (
  featurePatternSettings: FeaturePatternTrussSettings,
  componentName: string,
): FeaturePatternTrussSettings => {
  const { zoneSettings } = featurePatternSettings;
  return {
    ...featurePatternSettings,
    name: componentName,
    zoneSettings: zoneSettings.length > 1 ? [zoneSettings[0]] : zoneSettings,
  };
};

function calculateSpan(profile: Profile) {
  const allXs = profile.profileJoints.map((joint) => joint.location.x);
  return Math.max(...allXs) - Math.min(...allXs);
}

function getSeatCutGeometries(profile: Profile) {
  return profile.profileLines
    .filter((line) => line.profileLineType === 'SeatCut')
    .map((seatCut) => calculateProfileLineGeometry(profile, seatCut));
}

function extractProfileTrussSettingsFromComponentDesign(componentDesign: ComponentDesign): ProfileTrussSettings | null {
  if (
    !isTruss(componentDesign.component) ||
    !componentDesign.profile ||
    componentDesign.component.definitionType !== 'Profile'
  ) {
    return null;
  }

  const span = calculateSpan(componentDesign.profile);
  return {
    name: componentDesign.component.name,
    profile: componentDesign.profile,
    gableSettings: componentDesign.component.gableSettings,
    componentType: componentDesign.component.componentType,
    definitionType: 'Profile' as const,
    span,
    leftSeatCut:
      getSeatCutGeometries(componentDesign.profile)
        .filter((geometry) => distanceEquals(geometry.basePoint.x, 0) || distanceEquals(geometry.endPoint.x, 0))
        .at(0)?.length ?? 3.5,
    rightSeatCut:
      getSeatCutGeometries(componentDesign.profile)
        .filter((geometry) => distanceEquals(geometry.basePoint.x, span) || distanceEquals(geometry.endPoint.x, span))
        .at(0)?.length ?? 3.5,
  };
}

export const regenerateActiveComponentFromSettings = (): ThunkActionVoid => async (dispatch, getState) => {
  const context = getState().application.openContext;
  const projectGuid = context?.type === 'project' ? context.projectGuid : null;

  const initialComponentDesign = getActiveComponentDesign(getState());
  if (!initialComponentDesign) throw new Error('Expected an active component design');

  let designSettings: ProfileTrussSettings | FeaturePatternTrussSettings;
  const { component, materialSelection } = initialComponentDesign;
  const { componentType, definitionType } = component;

  let profileDefaults: ProfileDefaults | null = null;

  const loadSettingsPromise = dispatch(loadLoadSettings(initialComponentDesign.guid));
  const analysisSettingsPromise = dispatch(loadAnalysisSettings(initialComponentDesign.guid));
  const materialSettingsPromise = dispatch(loadMaterialSettingsForRegeneratingComponent(initialComponentDesign.guid));

  const jackConnections = Object.values(getState().jackGirderConnections.data).filter(
    (connection) => connection.girderGuid === initialComponentDesign.guid,
  );
  const jackGuids = uniq(jackConnections.map((connection) => connection.jackGuid));

  const jackInformationPromise = designService.componentDesigns.postGetJackInformation(jackGuids);

  if (isProfileTruss(component)) {
    const profileDefaultsPromise = projectGuid
      ? dispatch(loadProfileDefaults(projectGuid))
      : getDefaultProfileDefaults();

    if (!initialComponentDesign.profile) {
      await dispatch(createProfileOfActiveComponentDesign());
    }

    profileDefaults = await profileDefaultsPromise;

    const updatedComponentDesign = getState().componentDesigns.data[initialComponentDesign.guid];
    if (!updatedComponentDesign) throw new Error('The component is no longer loaded.');

    let profileTrussSettings = extractProfileTrussSettingsFromComponentDesign(updatedComponentDesign);
    if (!profileTrussSettings) {
      throw new Error('The profile truss settings were not defined.');
    }
    profileTrussSettings = cleanProfileTrussSettings(profileTrussSettings);

    // The name is a special case where we want to use the name of the current component, not that of the settings.
    profileTrussSettings = { ...profileTrussSettings, name: component.name };

    designSettings = profileTrussSettings;
  } else if (isFeaturePatternTruss(component)) {
    if (!component.settings) throw new Error('Settings should be set.');
    designSettings = adjustFeaturePatternTrussSettingsForInsertion(component.settings, component.name);
  } else {
    throw new Error('Copying wall settings is not implemented.');
  }

  const jackInformation = await jackInformationPromise;

  // If the truss doesn't have jack information, just remove it for now
  const jackGuidsWithJackInformation = new Set(jackGuids.filter((jackGuid) => jackInformation[jackGuid]));
  const jackConnectionsWithJackInformation = jackConnections.filter((connection) =>
    jackGuidsWithJackInformation.has(connection.jackGuid),
  );
  const nonNullJackInformation = Object.fromEntries(
    Object.entries(jackInformation)
      .map(([jackGuid, jackInformation]) => (jackInformation ? [jackGuid, jackInformation] : null))
      .filter(notNullish),
  );

  const shape = getShapeFromDefinitionType(definitionType);

  dispatch(
    applicationOnRegeneratingComponentDesign({
      parentGuid: null,
      componentType,
      definitionType,
      shape,
      stage: 'ShapeDetails',
      designSettings,
      profileDefaults,
      materialSelection,
      oldComponentDesign: initialComponentDesign,
      loadSettings: await loadSettingsPromise,
      analysisSettings: await analysisSettingsPromise,
      materialSettings: await materialSettingsPromise,
      jackConnections: jackConnectionsWithJackInformation,
      jackInformation: nonNullJackInformation,
    }),
  );

  await dispatch(updateInsertingTrussFromSettings({ preview: true, focusCameraOnTruss: true }));
};

/**
 * We need to make sure both profile and feature pattern truss settings exist in case the user switches definition type.
 * Therefore, we load the material settings for the active component, and fill in the other definition type's material
 * settings from the component design's parent if applicable.
 */
const loadMaterialSettingsForRegeneratingComponent =
  (componentDesignGuid: string): AsyncThunkActionReturn<MaterialSettingsPair> =>
  async (dispatch, getState) => {
    const componentMaterialSettings = await dispatch(loadMaterialSettings(componentDesignGuid));

    const parentGuids = getParentGuids(getState(), componentDesignGuid);
    // The behavior is ambiguous when there are multiple parents, so we just pick the first one.
    const parentGuid = parentGuids.length >= 1 ? parentGuids[0] : null;
    const parentOrDefaultMaterialSettings =
      parentGuid != null
        ? await dispatch(loadMaterialSettingsAndPrices(parentGuid))
        : await dispatch(loadDefaultMaterialSettingsAndPrices());

    // Give preference to `componentMaterialSettings`
    return { ...parentOrDefaultMaterialSettings, ...componentMaterialSettings };
  };

export function getShapeFromDefinitionType(definitionType: ComponentDefinitionType): ComponentInsertionShape {
  switch (definitionType) {
    case 'Profile':
      return 'FreeformProfile';
    case 'FeaturePattern':
      return 'FeaturePattern';
    case 'Wall':
      throw new Error('Walls are not supported.');
    default:
      assertExhaustiveSwitch(definitionType);
  }
}

export const commitInsertComponentDesign = (): AsyncThunkActionVoid => async (dispatch, getState) => {
  const { insertingObjectInfo, openContext } = getState().application;

  if (insertingObjectInfo?.type !== 'componentDesign') return;

  const { oldComponentDesign } = insertingObjectInfo;

  let componentDesign: ComponentDesign;
  if (!oldComponentDesign) {
    // We're creating a new component design

    await dispatch(updateInsertingTrussFromSettings({ preview: false }));

    const { insertingObjectInfo } = getState().application;
    if (insertingObjectInfo?.type !== 'componentDesign') return;
    const { loadedInfo, parentGuid } = insertingObjectInfo;

    if (!loadedInfo.isLoaded) return;

    const componentDesignGuid = loadedInfo.guid;
    componentDesign = getState().componentDesigns.data[componentDesignGuid];

    if (parentGuid != null) {
      try {
        await dispatch(
          runWithSpinner('Updating project...', () =>
            dispatch(addComponentDesignToProject(componentDesign.guid, parentGuid)),
          ),
        );
      } catch {
        dispatch(enqueueError('Failed to add the component to the project.'));
        return;
      }
    } else if (isParagonJobOpen(getState())) {
      try {
        await dispatch(
          runWithSpinner('Updating job...', () =>
            dispatch(addNewComponentDesignsToJobLayoutModel([componentDesign.guid])),
          ),
        );
      } catch {
        dispatch(enqueueError('Failed to add the component to the job.'));
        return;
      }
    }
  } else {
    // We're regenerating a component design from settings

    // Regenerating the truss from settings should take precedence over any pending save requests, so we wait for those
    // to finish to avoid any race conditions.
    await dispatch(
      runWithSpinner('Waiting for pending saves...', () =>
        waitForComponentDesignToFinishSaving(oldComponentDesign.guid),
      ),
    );

    let response: RegenerateTrussFromSettingsResponse;
    try {
      const organizationSettings = await dispatch(loadOrganizationSettingsIfNeeded());
      response = await dispatch(
        runWithSpinner('Saving component changes...', () =>
          regenerateComponentDesignFromSettings(
            getState(),
            insertingObjectInfo,
            oldComponentDesign,
            organizationSettings,
          ),
        ),
      );
    } catch (error) {
      dispatch(enqueueError('Failed to save component changes.'));
      return;
    }

    dispatch(receiveRegenerateFromSettingsResponse(response));
    componentDesign = response.componentDesign;

    try {
      await dispatch(
        runWithSpinner('Loading additional component information...', () =>
          dispatch(loadAdditionalComponentDesignInformation(componentDesign, { isPersisted: true })),
        ),
      );
    } catch {
      dispatch(enqueueWarning('Failed to load additional component information.'));
    }
  }

  const {
    loadSettings,
    loadSettingsState,
    analysisSettings,
    analysisSettingsState,
    materialSettings,
    materialSettingsState,
  } = insertingObjectInfo;

  // If in a project context, project inheritance will take care of missing settings. If regenerating a component, the
  //   settings should already exist. Otherwise, we need to ensure that every type of setting was either provided or is
  //   set to default settings.
  const loadSettingsToSave =
    (openContext?.type !== 'project' && !oldComponentDesign) || loadSettingsState === 'modified' ? loadSettings : null;
  const analysisSettingsToSave =
    (openContext?.type !== 'project' && !oldComponentDesign) || analysisSettingsState === 'modified'
      ? analysisSettings
      : null;
  const materialSettingsToSave =
    (openContext?.type !== 'project' && !oldComponentDesign) || materialSettingsState === 'modified'
      ? materialSettings
      : null;

  if (loadSettingsToSave || analysisSettingsToSave || materialSettingsToSave) {
    await dispatch(
      runWithSpinner('Saving component settings...', async () => {
        if (loadSettingsToSave) {
          await dispatch(
            saveLoadSettings(loadSettingsToSave, {
              action: SaveAction.ActualForSingleComponent,
              modelGuid: componentDesign.guid,
            }),
          );
        }

        if (analysisSettingsToSave) {
          await dispatch(
            saveAnalysisSettings(analysisSettingsToSave, {
              action: SaveAction.ActualForSingleComponent,
              modelGuid: componentDesign.guid,
            }),
          );
        }

        if (materialSettingsToSave) {
          await dispatch(
            saveMaterialSettings(materialSettingsToSave, {
              action: SaveAction.ActualForSingleComponent,
              modelGuid: componentDesign.guid,
            }),
          );
        }
      }),
    );
  }

  // We skip loading these from the server when loading the component design because the component design has not been
  // added to the project at the time we receive the component design. We just use the ones that are in state since we
  // already have them loaded for insertion. Material settings could probably also be set here, but they're not loaded
  // when receiving the component design and material settings scare me in general.
  dispatch(setLoadSettingsForComponentDesigns(componentDesign.guid, loadSettings));
  dispatch(setAnalysisSettingsForComponentDesigns(componentDesign.guid, analysisSettings));

  dispatch(clearInsertingObject());

  dispatch(selectItem(componentDesign, 'componentDesign'));
};

async function regenerateComponentDesignFromSettings(
  state: RootState,
  insertingObjectInfo: InsertingComponentDesignInfo,
  oldComponentDesign: ComponentDesign,
  organizationSettings: OrganizationSettings | null,
): Promise<RegenerateTrussFromSettingsResponse> {
  const organization = getOrganizationForOpenContext(state);

  const lumberLists = getLumberLists(state);
  if (!lumberLists)
    throw new Error('Material lists not loaded. They should have been loaded before starting component insertion.');
  const lumberList = lumberLists[insertingObjectInfo.materialSelection.lumberListGuid];

  const girderConnections = Object.values(state.jackGirderConnections.data).filter(
    (connection) => connection.jackGuid === oldComponentDesign.guid,
  );

  switch (insertingObjectInfo.definitionType) {
    case 'FeaturePattern': {
      const featurePatternSettings = insertingObjectInfo.designSettings as FeaturePatternTrussSettings;
      const materialSettings = insertingObjectInfo.materialSettings.featurePatternMaterialSettings;
      if (!materialSettings) throw new Error('Expected material settings to be set.');
      const stockLengthPrices = getLoadedStockLengthPricesForMaterialSettings(state, materialSettings);

      return await designService.componentDesigns.regenerateFeaturePatternTrussFromSettings({
        createFeaturePatternTrussRequest: {
          featurePatternTrussSettings: featurePatternSettings,
          materialSettings,
          organization,
          materialSelection: insertingObjectInfo.materialSelection,
          memberAnalysisInputs: {
            lumberList,
            lumberStockLengthPrices: stockLengthPrices,
            loadSettings: insertingObjectInfo.loadSettings,
            jackConnections: insertingObjectInfo.jackConnections,
            jackInformation: insertingObjectInfo.jackInformation,
            analysisSettings: insertingObjectInfo.analysisSettings,
            additionalLoads: insertingObjectInfo?.oldComponentDesign?.additionalLoads ?? null,
          },
        },
        componentDesign: oldComponentDesign,
        girderConnections,
      });
    }

    case 'Profile': {
      const profileTrussSettings = insertingObjectInfo.designSettings as ProfileTrussSettings;
      const materialSettings = insertingObjectInfo.materialSettings.profileMaterialSettings;
      if (!materialSettings) throw new Error('Expected material settings to be set.');
      const stockLengthPrices = getLoadedStockLengthPricesForMaterialSettings(state, materialSettings);
      const { scarfCutThreshold } = organizationSettings ?? DEFAULT_ORGANIZATION_SETTINGS;

      return await designService.componentDesigns.regenerateProfileTrussFromSettings({
        createProfileTrussRequest: {
          profileTrussSettings,
          materialSettings,
          scarfCutThreshold,
          organization,
          materialSelection: insertingObjectInfo.materialSelection,
          memberAnalysisInputs: {
            lumberList,
            lumberStockLengthPrices: stockLengthPrices,
            loadSettings: insertingObjectInfo.loadSettings,
            jackConnections: insertingObjectInfo.jackConnections,
            jackInformation: insertingObjectInfo.jackInformation,
            analysisSettings: insertingObjectInfo.analysisSettings,
            additionalLoads: insertingObjectInfo?.oldComponentDesign?.additionalLoads ?? null,
          },
          webbingInfo: insertingObjectInfo.webbingInfo ?? null,
        },
        componentDesign: oldComponentDesign,
        girderConnections,
      });
    }

    case 'Wall':
      throw new Error('Wall truss are not yet implemented');

    default:
      assertExhaustiveSwitch(insertingObjectInfo.definitionType);
  }
}
